import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { promptApi } from '@/services/promptApi';
import { useEntity } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, FileText, Play } from 'lucide-react';
import { BrandedSpinner } from '@/components/common/BrandedLoading';
import { EmptyState } from '@/components/common';
import type { PromptSummary, PromptDetail, RunPromptResponse } from '@/types/prompt';

function extractOutputText(runResult: RunPromptResponse | null): string | null {
  if (!runResult) return null;
  const r = runResult as Record<string, unknown>;
  if (typeof r.output === 'string') return r.output;
  if (typeof r.result === 'string') return r.result;
  if (typeof r.text === 'string') return r.text;
  return JSON.stringify(runResult, null, 2);
}

export const PromptWriteTestPage: React.FC = () => {
  const [prompts, setPrompts] = useState<PromptSummary[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [selectedKeyOrId, setSelectedKeyOrId] = useState<string>('');
  const [customPromptText, setCustomPromptText] = useState('');
  const [runResult, setRunResult] = useState<RunPromptResponse | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    promptApi
      .list({ page: 0, size: 100 })
      .then((res) => {
        if (!cancelled) setPrompts(res.content ?? []);
      })
      .catch(() => {
        if (!cancelled) setPrompts([]);
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const {
    data: selectedPrompt,
    isLoading: isLoadingPrompt,
  } = useEntity<PromptDetail>({
    fetchFn: (keyOrId: string) => promptApi.get(keyOrId),
    entityId: selectedKeyOrId || undefined,
    entityName: 'prompt',
    enabled: !!selectedKeyOrId,
  });

  const docId = selectedPrompt?.docId ?? selectedPrompt?.id;
  const outputText = useMemo(() => extractOutputText(runResult), [runResult]);

  const handleRun = async () => {
    if (!docId) {
      setRunError('Select a prompt first.');
      return;
    }
    setRunError(null);
    setRunResult(null);
    setIsRunning(true);
    try {
      const result = await promptApi.run(docId, {
        variables: customPromptText.trim()
          ? { custom_prompt: customPromptText.trim(), user_prompt: customPromptText.trim() }
          : undefined,
      });
      setRunResult(result);
    } catch (e) {
      const err = e as { message?: string };
      setRunError(err?.message ?? 'Failed to run prompt');
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" className="shrink-0 mt-0.5" asChild>
          <Link to="/admin/prompts">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="min-w-0">
          <p className="text-sm text-muted-foreground">Prompt Library</p>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight mt-0.5">
            Write & test prompt
          </h1>
          <p className="text-muted-foreground mt-1.5">
            Choose a prompt template, write or paste your text, and run to see the model output.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Write & test prompt
          </CardTitle>
          <CardDescription>
            Select a prompt from the library, then enter your custom prompt text below and run.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-1.5">
            <Label htmlFor="prompt-select" className="text-sm font-medium">Prompt template</Label>
            {listLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                <BrandedSpinner />
                Loading prompts…
              </div>
            ) : (
              <Select value={selectedKeyOrId} onValueChange={setSelectedKeyOrId}>
                <SelectTrigger id="prompt-select" className="max-w-md">
                  <SelectValue placeholder="Select a prompt to use…" />
                </SelectTrigger>
                <SelectContent>
                  {prompts.length === 0 && (
                    <SelectItem value="__none__" disabled>
                      No prompts available
                    </SelectItem>
                  )}
                  {prompts.map((p) => (
                    <SelectItem key={p.id} value={p.key ?? p.id}>
                      {p.name ?? p.key ?? p.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {selectedKeyOrId && isLoadingPrompt && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <BrandedSpinner sizeClassName="h-3 w-3" /> Loading prompt…
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="custom-prompt" className="text-sm font-medium">Your prompt</Label>
            <Textarea
              id="custom-prompt"
              value={customPromptText}
              onChange={(e) => setCustomPromptText(e.target.value)}
              placeholder="Enter or paste the prompt you want to test..."
              className="min-h-[200px] font-mono text-sm resize-y"
              rows={8}
            />
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button
              onClick={handleRun}
              disabled={isRunning || !selectedKeyOrId || selectedKeyOrId === '__none__'}
              size="lg"
            >
              {isRunning ? (
                <>
                  <BrandedSpinner className="mr-2" />
                  Running…
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Run prompt
                </>
              )}
            </Button>
            {!selectedKeyOrId && (
              <p className="text-sm text-muted-foreground">
                Select a prompt template above first.
              </p>
            )}
          </div>

          {runError && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
              {runError}
            </div>
          )}

          {outputText !== null && (
            <div className="space-y-2 pt-2 border-t">
              <Label className="text-sm font-medium">Output</Label>
              <pre className="rounded-lg border bg-muted/50 p-4 text-sm overflow-x-auto max-h-[400px] overflow-y-auto whitespace-pre-wrap font-mono">
                {outputText}
              </pre>
            </div>
          )}
        </CardContent>
      </Card>

      {!listLoading && prompts.length === 0 && (
        <EmptyState
          title="No prompts available"
          description="Load the prompt library or add prompts to use Write & test."
          icon={FileText}
          actions={[
            {
              label: 'View Prompt Library',
              onClick: () => window.location.assign('/admin/prompts'),
              variant: 'outline',
            },
          ]}
        />
      )}
    </div>
  );
};

export default PromptWriteTestPage;
